import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import isEqual from 'lodash/isEqual';
import './score-board.scss';
import { create, nextRepaint } from '../../utils/dom';
import {
    CLASS_BLOCK_NAME,
    OPEN_BETS_CLASS_NAME,
    SCORE_COLUMN_CLASS_BLOCK_NAME,
    SCORE_FIELD_CLASS_BLOCK_NAME,
    MATCH_STATUS_FIELD_CLASS_BLOCK_NAME,
    MATCH_STATUS_OPEN_BETS_CLASS_NAME,
    SCORE_COLUMNS_CLASS_BLOCK_NAME,
    FOOTBALL,
    RUGBY_LEAGUE,
    RUGBY_UNION,
    GAA_HURLING,
    GAA_FOOTBALL,
    MMA,
    TENNIS,
    BADMINTON,
    BASKETBALL,
    ICE_HOCKEY,
    AMERICAN_FOOTBALL,
    SNOOKER,
    BOXING,
    DARTS,
    CRICKET,
    GOLF,
    BASEBALL,
    HANDBALL,
    VOLLEYBALL,
    ESPORTS,
    TABLE_TENNIS,
    AUSTRALIAN_RULES,
} from './constants';
import {
    footballNormalizer,
    rugbyLeagueNormalizer,
    rugbyUnionNormalizer,
    gaaHurlingNormalizer,
    gaaFootballNormalizer,
    tennisNormalizer,
    badmintonNormalizer,
    basketballNormalizer,
    iceHockeyNormalizer,
    americanFootballNormalizer,
    snookerNormalizer,
    boxingNormalizer,
    dartsNormalizer,
    cricketNormalizer,
    golfNormalizer,
    baseballNormalizer,
    handballNormalizer,
    volleyballNormalizer,
    eSportsNormaliser,
    tableTennisNormalizer,
    mmaNormalizer,
    australianRulesNormalizer,
} from './normalizers';

/**
 * Holds the strategies for each sport slug
 * @type {Object}
 */
const NormalizeStrategies = {
    [FOOTBALL]: footballNormalizer,
    [RUGBY_LEAGUE]: rugbyLeagueNormalizer,
    [RUGBY_UNION]: rugbyUnionNormalizer,
    [GAA_HURLING]: gaaHurlingNormalizer,
    [GAA_FOOTBALL]: gaaFootballNormalizer,
    [MMA]: mmaNormalizer,
    [TENNIS]: tennisNormalizer,
    [BADMINTON]: badmintonNormalizer,
    [BASKETBALL]: basketballNormalizer,
    [ICE_HOCKEY]: iceHockeyNormalizer,
    [AMERICAN_FOOTBALL]: americanFootballNormalizer,
    [SNOOKER]: snookerNormalizer,
    [BOXING]: boxingNormalizer,
    [DARTS]: dartsNormalizer,
    [CRICKET]: cricketNormalizer,
    [GOLF]: golfNormalizer,
    [BASEBALL]: baseballNormalizer,
    [HANDBALL]: handballNormalizer,
    [VOLLEYBALL]: volleyballNormalizer,
    [ESPORTS]: eSportsNormaliser,
    [TABLE_TENNIS]: tableTennisNormalizer,
    [AUSTRALIAN_RULES]: australianRulesNormalizer,
};

/**
 * Creates the score column element
 * @param  {Array} columnFields The fields for the score column
 * @param  {String} sport The sport slug
 * @param  {Bool} isUsFormat SHould display in us format
 * @return {Element} The score column element
 */
function createScoreColumn(columnFields, sport, isUsFormat) {
    const scoreColumn = create('div', `${SCORE_COLUMN_CLASS_BLOCK_NAME} ${SCORE_COLUMN_CLASS_BLOCK_NAME}--${sport}`);

    if (isUsFormat) {
        columnFields.reverse();
    }

    columnFields.forEach(({value, bemModifiers}) => {
        const modifiers = bemModifiers.map(modifier => `${SCORE_FIELD_CLASS_BLOCK_NAME}--${modifier}`);
        scoreColumn.appendChild(create('span', `${SCORE_FIELD_CLASS_BLOCK_NAME} ${modifiers.join(' ')}`, value));
    });

    return scoreColumn;
}

/**
 * Checks if the score board should be updated
 * @param  {Object} state The event object
 * @return {Boolean} Returns true if the state has extra_info or false if not
 */
function shouldUpdate(state) {
    return !isUndefined(state['extra_info']);
}

/**
 * Checks if the score should be updated
 * @param  {Object} nextState The normalized event object
 * @param  {Object} currentState The current normalized event object
 * @return {Boolean} Returns true if the score has changed or false if not
 */
function shouldUpdateScore(nextState, currentState) {
    if (isNull(currentState)) {
        return true;
    }

    return !isEqual(nextState.columns, currentState.columns);
}

/**
 * Checks if the status should be updated
 * @param  {Object} nextState The normalized event object
 * @param  {Object} currentState The current normalized event object
 * @return {Boolean} Returns true if the status has changed or false if not
 */
function shouldUpdateStatus(nextState, currentState) {
    if (isNull(currentState)) {
        return true;
    }

    return nextState.status !== currentState.status;
}

/**
 * Creates a score board
 * @param  {String} sport The sport slug
 * @param  {Object} initialState The event data
 * @param  {Bool} isUsFormat should display as us format
 * @return {Null|ScoreBoard} The score board API or null if it cannot create it
 */
export default function createScoreBoard(sport, initialState, isUsFormat = false) {
    if (isUndefined(NormalizeStrategies[sport])) {
        return null;
    }

    /**
     * The component dom element
     * @type {Element}
     */
    const el = create('div', `${CLASS_BLOCK_NAME} ${CLASS_BLOCK_NAME}--${sport} ${initialState.usesThreeMarketView ? `${CLASS_BLOCK_NAME}--three_market_view` : ''}`);

    /**
     * The columns wrapper dom element
     * @type {Element}
     */
    const columns = create('div', `${SCORE_COLUMNS_CLASS_BLOCK_NAME} ${SCORE_COLUMNS_CLASS_BLOCK_NAME}--${sport}`);

    /**
     * The match status dom element
     * @type {Element}
     */
    const matchStatus = create('span', `${MATCH_STATUS_FIELD_CLASS_BLOCK_NAME} ${MATCH_STATUS_FIELD_CLASS_BLOCK_NAME}--${sport}`);

    /**
     * The current score board state
     * @type {Object}
     */
    let currentState = null;

    /**
     * The normalizer for the sport scores
     * @type {Function}
     */
    const normalize = NormalizeStrategies[sport];

    el.appendChild(columns);

    el.appendChild(matchStatus);

    update(initialState);

    /**
     * Updates the score board
     * @param  {Object} nextEventState The event data
     * @return {void}
     */
    function update(nextEventState) {

        if (shouldUpdate(nextEventState)) {
            const nextState = normalize(nextEventState, currentState);

            if (shouldUpdateScore(nextState, currentState)) {
                nextRepaint(() => {
                    while (columns.firstChild) {
                        columns.removeChild(columns.firstChild);
                    }

                    nextState.columns.forEach((column) => columns.appendChild(createScoreColumn(column, sport, isUsFormat)));
                });
            }

            if (shouldUpdateStatus(nextState, currentState)) {
                nextRepaint(() => {
                    matchStatus.innerHTML = nextState.status;
                });
            }

            currentState = {...nextState};
        }

    }

    /**
     * Adds the open bets modifier class name
     * @return {void}
     */
    function addOpenBetsModifier() {
        el.classList.add(OPEN_BETS_CLASS_NAME);
        matchStatus.classList.add(MATCH_STATUS_OPEN_BETS_CLASS_NAME);
    }

    /**
     * Removes the open bets modifier class name
     * @return {void}
     */
    function removeOpenBetsModifier() {
        el.classList.remove(OPEN_BETS_CLASS_NAME);
        matchStatus.classList.remove(MATCH_STATUS_OPEN_BETS_CLASS_NAME);
    }

    /**
     * ScoreBoard API
     */
    return {
        el,
        matchStatus,
        update,
        addOpenBetsModifier,
        removeOpenBetsModifier,
    };
}
